import { useCallback, useReducer, useRef } from 'react'
import { logDebug, logError } from '../../errors/utils/errors'
import {
  isWarming,
  type Decision,
  type DecisionRequest,
  type PolicyEngineApiClient,
  type PolicyEvaluation,
} from '../api/v1/client/services/policy-engine'
import { useApiClient } from './api-client'

/** A check that takes longer resolves to null, so a slow engine cannot hold
 *  up the action that waits for it. */
const POLICY_EVALUATION_TIMEOUT_MS = 5000

export type PolicyEvaluationStatus =
  | 'idle'
  | 'checking'
  | 'done'
  | 'warming'
  | 'error'

export interface UsePolicyEvaluationResult {
  /** Imperative trigger — call on an action (e.g. a drop). Resolves to the
   *  decision, or null when there is no verdict: on error, after a timeout,
   *  while the tenant is warming, or when the policy surface is unavailable. */
  evaluate: (request: DecisionRequest) => Promise<Decision | null>
  /** Last successful decision. Persists across a later failed evaluate, so
   *  branch on `status` — don't read `decision` alone in a gate. */
  decision: Decision | null
  status: PolicyEvaluationStatus
  error: Error | null
  reset: () => void
}

interface State {
  decision: Decision | null
  status: PolicyEvaluationStatus
  error: Error | null
}

type Action =
  | { type: 'CHECK_START' }
  | { type: 'CHECK_SUCCESS'; payload: Decision }
  | { type: 'CHECK_WARMING' }
  | { type: 'CHECK_ERROR'; payload: Error }
  | { type: 'RESET' }

const initialState: State = { decision: null, status: 'idle', error: null }

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'CHECK_START':
      return { ...state, status: 'checking', error: null }
    case 'CHECK_SUCCESS':
      return { decision: action.payload, status: 'done', error: null }
    case 'CHECK_WARMING':
      return { ...state, status: 'warming', error: null }
    case 'CHECK_ERROR':
      return { ...state, status: 'error', error: action.payload }
    case 'RESET':
      return initialState
  }
}

let warnedMissing = false

async function evaluateWithTimeout(
  policy: PolicyEngineApiClient,
  request: DecisionRequest,
): Promise<PolicyEvaluation> {
  const controller = new AbortController()
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      // Reject before the abort, so the race settles with this error and not
      // with the request's own abort error.
      const timeoutError = new Error(
        `Policy evaluation for tenant ${request.tenantId} timed out after ${POLICY_EVALUATION_TIMEOUT_MS} ms`,
      )
      logError(timeoutError, {
        tenantId: request.tenantId,
        timeoutMs: POLICY_EVALUATION_TIMEOUT_MS,
      })
      reject(timeoutError)
      controller.abort()
    }, POLICY_EVALUATION_TIMEOUT_MS)
  })
  try {
    return await Promise.race([
      policy.evaluate(request, { signal: controller.signal }),
      timeout,
    ])
  } finally {
    clearTimeout(timer)
  }
}

export const usePolicyEvaluation = (): UsePolicyEvaluationResult => {
  const apiClient = useApiClient()
  const [state, dispatch] = useReducer(reducer, initialState)
  const latestRequestId = useRef(0)

  const evaluate = useCallback(
    async (request: DecisionRequest): Promise<Decision | null> => {
      // Degrade cleanly if the host hasn't shipped the policy surface yet.
      if (!apiClient.policy) {
        if (!warnedMissing) {
          warnedMissing = true
          console.info(
            '[usePolicyEvaluation] apiClient.policy unavailable on this host; skipping evaluation.',
          )
        }
        return null
      }
      const requestId = ++latestRequestId.current
      dispatch({ type: 'CHECK_START' })
      try {
        const evaluation = await evaluateWithTimeout(apiClient.policy, request)
        if (isWarming(evaluation)) {
          logDebug(
            'The policy engine does not yet hold complete data for this tenant, so the policy check has no verdict.',
            {
              tenantId: evaluation.tenantId,
              unbaselinedDomains: evaluation.unbaselinedDomains,
            },
          )
        }
        const decision = isWarming(evaluation) ? null : evaluation
        if (requestId === latestRequestId.current) {
          dispatch(
            decision
              ? { type: 'CHECK_SUCCESS', payload: decision }
              : { type: 'CHECK_WARMING' },
          )
        }
        return decision
      } catch (err) {
        // JS can throw non-Error values; normalize so `error` state is always an Error.
        const error = err instanceof Error ? err : new Error(String(err))
        if (requestId === latestRequestId.current) {
          dispatch({ type: 'CHECK_ERROR', payload: error })
        }
        return null
      }
    },
    [apiClient],
  )

  const reset = useCallback(() => {
    latestRequestId.current += 1
    dispatch({ type: 'RESET' })
  }, [])

  return { ...state, evaluate, reset }
}
